import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => { await logout(); navigate('/'); };

  const navLinks = [
    { label: 'Home', to: '/#home' },
    { label: 'About Us', to: '/#about' },
    { label: 'Courses', to: '/#programs' },
    { label: 'Projects', to: '/#projects' },
  ];

  const scrollTo = (hash) => {
    setMobileOpen(false);
    if (location.pathname !== '/') { navigate('/' + hash); return; }
    const id = hash.replace('#', '');
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav className="bb-navbar" data-testid="navbar">
      <div className="w-[90%] max-w-[1200px] mx-auto flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 no-underline" data-testid="nav-logo">
          <div className="w-[50px] h-[50px] rounded-lg overflow-hidden flex items-center justify-center">
            <img src="/bb-logo.jpeg" alt="BuildBotics" className="w-full h-full object-contain" />
          </div>
        </Link>

        {/* Desktop Nav Links */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <button key={link.label}
              onClick={() => scrollTo(link.to.split('/').pop())}
              className="font-medium text-base relative text-[var(--text)] hover:text-[var(--primary)] transition-colors bg-transparent border-none cursor-pointer"
              style={{ fontFamily: 'Inter' }}
              data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}>
              {link.label}
            </button>
          ))}

          {user && user.role ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="font-medium text-base text-[var(--text)] hover:text-[var(--primary)] transition-colors bg-transparent border-none cursor-pointer flex items-center gap-2" data-testid="user-menu-btn">
                  <i className="fa-solid fa-user text-sm" /> {user.name}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white border-gray-200 text-gray-900 min-w-[180px]">
                <DropdownMenuItem onClick={() => navigate(user.role === 'admin' ? '/admin' : '/dashboard')} className="cursor-pointer" data-testid="nav-dashboard-link">
                  <i className="fa-solid fa-gauge mr-2 text-sm" /> Dashboard
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-500" data-testid="nav-logout-btn">
                  <i className="fa-solid fa-right-from-bracket mr-2 text-sm" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <button onClick={() => navigate('/login')} className="font-medium text-base text-[var(--text)] hover:text-[var(--primary)] transition-colors bg-transparent border-none cursor-pointer" data-testid="nav-login-btn">
                Log In
              </button>
              <a href="#contact" onClick={(e) => { e.preventDefault(); scrollTo('#contact'); }}
                className="bb-btn bb-btn-primary !py-2.5 !px-5 !text-sm !shadow-[4px_4px_0px_rgba(15,23,42,0.9)]" data-testid="nav-contact-btn">
                CONTACT
              </a>
            </>
          )}
        </div>

        {/* Mobile hamburger */}
        <button className="md:hidden text-2xl text-[var(--text)] bg-transparent border-none cursor-pointer" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn">
          <i className={mobileOpen ? 'fa-solid fa-xmark' : 'fa-solid fa-bars'} />
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 bg-white z-[2000] flex flex-col items-center justify-center gap-8" data-testid="mobile-menu">
          <button className="absolute top-7 right-7 text-2xl text-[var(--text)] bg-transparent border-none cursor-pointer" onClick={() => setMobileOpen(false)}>
            <i className="fa-solid fa-xmark" />
          </button>
          {navLinks.map((link) => (
            <button key={link.label}
              onClick={() => scrollTo(link.to.split('/').pop())}
              className="text-lg font-medium text-[var(--text)] bg-transparent border-none cursor-pointer hover:text-[var(--primary)]">
              {link.label}
            </button>
          ))}
          {user && user.role ? (
            <>
              <button onClick={() => { navigate(user.role === 'admin' ? '/admin' : '/dashboard'); setMobileOpen(false); }}
                className="text-lg font-medium text-[var(--text)] bg-transparent border-none cursor-pointer">Dashboard</button>
              <button onClick={handleLogout} className="text-lg font-medium text-red-500 bg-transparent border-none cursor-pointer">Logout</button>
            </>
          ) : (
            <>
              <button onClick={() => { navigate('/login'); setMobileOpen(false); }} className="text-lg font-medium text-[var(--text)] bg-transparent border-none cursor-pointer">Log In</button>
              <a href="#contact" onClick={(e) => { e.preventDefault(); scrollTo('#contact'); }} className="bb-btn bb-btn-primary !text-sm">CONTACT</a>
            </>
          )}
        </div>
      )}
    </nav>
  );
}
